import { useMemo, useState, useEffect } from "react";
import { ChevronDown, Circle, CheckCircle, MinusCircle } from "lucide-react";
import { GetTopicsCourseType } from "@packages/api/madlenAPI.schemas";
import {
  useGetLearningOutcomes,
  useGetMaarifLearningOutcomes,
} from "@packages/api/madlenAPI";
import { Button } from "@core/button";
import { Text } from "@core/text";
import { useTranslation } from "react-i18next";
interface UnitOutcomeSelectorProps {
  grade?: number;
  courseType: GetTopicsCourseType;
  selectedOutcomes: number[];
  isMaarif?: boolean;
  setIsMaarif?: (value: boolean) => void;
  setSelectedOutcomes: (outcomeIds: number[], outcomeTexts?: string[]) => void;
}

export const UnitOutcomeSelector = ({
  grade,
  courseType,
  selectedOutcomes,
  isMaarif = false,
  setSelectedOutcomes,
}: UnitOutcomeSelectorProps) => {
  const { i18n, t } = useTranslation();
  const shouldUseMaarif = isMaarif;

  const { data: maarifOutcomesData, isLoading: isMaarifLoading } =
    useGetMaarifLearningOutcomes(
      shouldUseMaarif
        ? {
            grade,
            courseType,
          }
        : undefined,
      {
        query: {
          enabled: shouldUseMaarif,
        },
      },
    );

  const { data: regularOutcomes, isLoading: isRegularLoading } =
    useGetLearningOutcomes(
      !shouldUseMaarif
        ? {
            grade,
            courseType,
          }
        : undefined,
      {
        query: {
          enabled: !shouldUseMaarif,
        },
      },
    );

  const [searchTerm, setSearchTerm] = useState("");
  const [expandedUnit, setExpandedUnit] = useState<string | null>(null);

  const [prevGrade, setPrevGrade] = useState(grade);
  const [prevCourseType, setPrevCourseType] = useState(courseType);
  const [prevIsMaarif, setPrevIsMaarif] = useState(isMaarif);

  useEffect(() => {
    setPrevGrade(grade);
    setPrevCourseType(courseType);
    setPrevIsMaarif(isMaarif);
  }, [grade, courseType, isMaarif]);

  const allUnitsAsOption = useMemo(() => {
    const result: {
      label: string;
      options: { id: number; label: string; description?: string[] }[];
    }[] = [];

    if (shouldUseMaarif && maarifOutcomesData?.units) {
      maarifOutcomesData.units.forEach((unit) => {
        const validOutcomes =
          unit.outcomes
            ?.filter((outcome) => outcome.id != null)
            .map((outcome) => ({
              id: outcome.id!,
              label: outcome.outcome || "",
              description: outcome.outcomeExplanationList,
            })) || [];

        if (validOutcomes.length > 0) {
          result.push({
            label: unit.unit || unit.description || "",
            options: validOutcomes,
          });
        }
      });
    } else if (regularOutcomes?.units) {
      regularOutcomes.units.forEach((unit) => {
        const validOutcomes =
          unit.outcomes
            ?.filter((outcome) => outcome.id != null)
            .map((outcome) => ({
              id: outcome.id!,
              label: outcome.outcome || "",
              description: outcome.outcomeExplanationList,
            })) || [];

        if (validOutcomes.length > 0) {
          result.push({
            label: unit.unit,
            options: validOutcomes,
          });
        }
      });
    }
    return result;
  }, [maarifOutcomesData, regularOutcomes, shouldUseMaarif]);

  const filteredUnits = useMemo(() => {
    if (!searchTerm) return allUnitsAsOption;

    const lowercaseSearch = searchTerm.toLowerCase();

    const currentExpandedUnit = expandedUnit;

    const filteredResult = allUnitsAsOption
      .map((unit) => ({
        ...unit,
        options: unit.options.filter((outcome) =>
          outcome.label.toLowerCase().includes(lowercaseSearch),
        ),
      }))
      .filter(
        (unit) =>
          unit.label.toLowerCase().includes(lowercaseSearch) ||
          unit.options.length > 0,
      );

    if (
      currentExpandedUnit &&
      currentExpandedUnit !== "all" &&
      !filteredResult.some((unit) => unit.label === currentExpandedUnit) &&
      filteredResult.length > 0
    ) {
      const unitWithSelectedOutcome = filteredResult.find((unit) =>
        unit.options.some((option) => selectedOutcomes.includes(option.id)),
      );

      if (unitWithSelectedOutcome) {
        setTimeout(() => setExpandedUnit(unitWithSelectedOutcome.label), 0);
      } else if (filteredResult.length > 0) {
        setTimeout(() => setExpandedUnit(filteredResult[0].label), 0);
      }
    }

    return filteredResult;
  }, [allUnitsAsOption, searchTerm, expandedUnit, selectedOutcomes]);

  const findUnitWithSelectedOutcomes = useMemo(() => {
    if (selectedOutcomes.length === 0 || filteredUnits.length === 0) {
      return null;
    }

    return (
      filteredUnits.find((unit) =>
        unit.options.some((option) => selectedOutcomes.includes(option.id)),
      )?.label || null
    );
  }, [selectedOutcomes, filteredUnits]);

  useEffect(() => {
    if (filteredUnits.length === 0) return;

    if (
      !expandedUnit ||
      !filteredUnits.some((unit) => unit.label === expandedUnit)
    ) {
      if (findUnitWithSelectedOutcomes) {
        setExpandedUnit(findUnitWithSelectedOutcomes);
      } else {
        setExpandedUnit(filteredUnits[0].label);
      }
    }
  }, [filteredUnits, findUnitWithSelectedOutcomes]);

  useEffect(() => {
    if (
      grade !== prevGrade ||
      courseType !== prevCourseType ||
      isMaarif !== prevIsMaarif
    ) {
      const validIds = allUnitsAsOption.flatMap((unit) =>
        unit.options.map((option) => option.id),
      );

      const stillValidOutcomes = selectedOutcomes.filter((id) =>
        validIds.includes(id),
      );

      if (stillValidOutcomes.length !== selectedOutcomes.length) {
        setSelectedOutcomes(stillValidOutcomes);
      }
    }
  }, [
    allUnitsAsOption,
    grade,
    courseType,
    isMaarif,
    prevGrade,
    prevCourseType,
    prevIsMaarif,
  ]);

  const isLoading = shouldUseMaarif ? isMaarifLoading : isRegularLoading;

  if (!allUnitsAsOption?.length || isLoading) {
    const shouldAnimate = isLoading && !allUnitsAsOption?.length;
    return (
      <div className="w-full border rounded-lg overflow-hidden dark:border-gray-700">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="border-b last:border-b-0 dark:border-gray-700"
          >
            <div className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 flex items-center justify-between">
              <div
                className={`h-4 w-32 rounded ${shouldAnimate && " bg-gray-200 dark:bg-gray-700 animate-pulse"}`}
              >
                {" "}
              </div>

              <div className="flex items-center space-x-2">
                <div
                  className={`h-3 w-8 rounded ${shouldAnimate && "bg-gray-200 dark:bg-gray-700 animate-pulse"}`}
                ></div>
                <div
                  className={`h-4 w-4 rounded ${shouldAnimate && " bg-gray-200 dark:bg-gray-700 animate-pulse"}`}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const toggleUnit = (unitLabel: string) => {
    setExpandedUnit((prev) => (prev === unitLabel ? null : unitLabel));
  };

  const isOutcomeSelected = (outcomeId: number) =>
    selectedOutcomes.includes(outcomeId);

  const getUnitSelectionState = (unit: (typeof filteredUnits)[0]) => {
    const selectedCount = unit.options.filter((outcome) =>
      selectedOutcomes.includes(outcome.id),
    ).length;
    if (selectedCount === 0) return "none";
    if (selectedCount === unit.options.length) return "all";
    return "some";
  };

  const getOutcomeNamesByIds = (outcomeIds: number[]) => {
    return allUnitsAsOption
      .flatMap((unit) => unit.options)
      .filter((outcome) => outcomeIds.includes(outcome.id))
      .map((outcome) => outcome.label);
  };

  const toggleAllTopicsInUnit = (unit: (typeof filteredUnits)[0]) => {
    const unitState = getUnitSelectionState(unit);
    const outcomeIds = unit.options.map((outcome) => outcome.id);

    if (unitState === "all") {
      const newSelectedOutcomes = selectedOutcomes.filter(
        (id) => !outcomeIds.includes(id),
      );
      setSelectedOutcomes(
        newSelectedOutcomes,
        getOutcomeNamesByIds(newSelectedOutcomes),
      );
    } else {
      const newSelectedOutcomes = [
        ...new Set([...selectedOutcomes, ...outcomeIds]),
      ];
      setSelectedOutcomes(
        newSelectedOutcomes,
        getOutcomeNamesByIds(newSelectedOutcomes),
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t("common.search_unit_or_topic")}
          className="w-full px-4 py-2 border rounded-lg dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>

      <div className="w-full border rounded-lg overflow-hidden dark:border-gray-700">
        {filteredUnits.map((unit, index) => {
          const unitState = getUnitSelectionState(unit);
          return (
            <div
              key={unit.label + index}
              className="border-b last:border-b-0 dark:border-gray-700"
            >
              {/* Unit Header */}
              <div className="flex">
                <Button
                  onClick={() => toggleAllTopicsInUnit(unit)}
                  variant="solid"
                  size="xs"
                  bgColor="bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  {unitState === "none" && (
                    <Circle size={16} className="text-gray-400" />
                  )}
                  {unitState === "some" && (
                    <MinusCircle size={16} className="text-orange-500" />
                  )}
                  {unitState === "all" && (
                    <CheckCircle size={16} className="text-orange-500" />
                  )}
                </Button>
                <Button
                  onClick={() => toggleUnit(unit.label)}
                  variant="solid"
                  size="md"
                  bgColor="bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                  fullWidth
                  className="flex justify-between"
                  aria-expanded={expandedUnit === unit.label}
                >
                  <Text variant="p" className="text-left">
                    {unit.label}
                  </Text>
                  <div className="flex items-center space-x-2">
                    <Text variant="p" status="secondary" size="sm">
                      {
                        unit.options.filter((outcome) =>
                          selectedOutcomes.includes(outcome.id),
                        ).length
                      }
                      /{unit.options.length}
                    </Text>
                    <ChevronDown
                      size={16}
                      className={`text-gray-400 dark:text-gray-500 transition-transform ${
                        expandedUnit === unit.label ? "rotate-180" : ""
                      }`}
                    />
                  </div>
                </Button>
              </div>

              {/* Unit Topics */}
              {(expandedUnit === "all" || expandedUnit === unit.label) && (
                <div className="relative">
                  <div className="bg-white dark:bg-gray-800 px-4 py-2 overflow-y-auto pb-6">
                    {unit.options.map((outcome) => (
                      <Button
                        data-testid="unit-outcome-selector-topic"
                        key={outcome.id}
                        onClick={() => {
                          if (selectedOutcomes.includes(outcome.id)) {
                            const newSelectedOutcomes = selectedOutcomes.filter(
                              (id) => id !== outcome.id,
                            );
                            setSelectedOutcomes(
                              newSelectedOutcomes,
                              getOutcomeNamesByIds(newSelectedOutcomes),
                            );
                          } else {
                            const newSelectedOutcomes = [
                              ...new Set([...selectedOutcomes, outcome.id]),
                            ];
                            setSelectedOutcomes(
                              newSelectedOutcomes,
                              getOutcomeNamesByIds(newSelectedOutcomes),
                            );
                          }
                        }}
                        variant="solid"
                        size="xs"
                        rounded="lg"
                        fullWidth
                        bgColor={
                          isOutcomeSelected(outcome.id)
                            ? "bg-orange-50 dark:bg-orange-900/30"
                            : "hover:bg-gray-100 dark:hover:bg-gray-700"
                        }
                        textColor={
                          isOutcomeSelected(outcome.id)
                            ? "text-orange-600 dark:text-orange-300"
                            : ""
                        }
                        className="my-0.5 text-left focus:outline-none"
                      >
                        <div className="space-y-2">
                          <p className="text-sm font-medium">{outcome.label}</p>
                          {outcome.description &&
                            outcome.description.length > 0 && (
                              <div className="space-y-1">
                                {outcome.description.map((desc, i) => (
                                  <div
                                    key={i}
                                    className="flex items-start gap-2"
                                  >
                                    <Text
                                      variant="p"
                                      status="secondary"
                                      size="xs"
                                      key={i}
                                    >
                                      - {desc}
                                    </Text>
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
